import { Box, Grid, IconButton, ListItemButton, ListItemText, Stack, Tooltip, Typography } from "@mui/material";
import { useHeaderPopup } from "../../Hooks";
import { HomeOutlined, MasterDataOutlined, MoreMenuOutlined, SettingOutlined } from "Icons/Icons";
import { CircularLoading } from "Common/Components/Wrappers";
import { useGetUserApplicationsQuery, useLazyGetAbsorbLmsLoginQuery } from "Services/Admin";
import { ApplicationAvatar, Snackbar } from "Common/Components";
import { useAuthorization } from "Common/Hooks";
import { useState } from "react";
import { AbsorbLmsRedirectDialog } from "./AbsorbLmsRedirectDialog";
import { SNACKBAR_SEVERITY } from "Common/Constants";
import { HeaderPopup } from "../../Components";

export const AppsMenu = () => {
  const { anchorRef, handleToggle, open, handleClose } = useHeaderPopup();
  const { user, loggedUserEmail, scope } = useAuthorization();
  const { role } = scope ?? {};

  // Dialog state for Absorb LMS redirect
  const [redirectDialog, setRedirectDialog] = useState<{
    open: boolean;
    url: string;
  }>({
    open: false,
    url: "",
  });

  // Snackbar state for loading message
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
  }>({
    open: false,
    message: "",
  });

  const handleRedirectDialogClose = () => {
    setRedirectDialog({
      open: false,
      url: "",
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleRedirectConfirm = () => {
    if (redirectDialog.url) {
      window.open(redirectDialog.url, "_blank");
      handleClose(new Event("click"));
    }
    handleRedirectDialogClose();
  };

  const { data, isFetching } = useGetUserApplicationsQuery({
    limit: 50,
    offset: 0,
    orderBy: "name",
    search: "",
    sortBy: "1",
  });

  const { data: apps } = data ?? {};

  const [getAbsorbLmsLogin] = useLazyGetAbsorbLmsLoginQuery();

  const coreApps = apps?.filter((app) => app.isCoreApplication) || [];
  const normalApps = apps?.filter((app) => !app.isCoreApplication) || [];

  const handleAppClick = (url?: string, appName?: string) => async (e: React.MouseEvent) => {
    e.preventDefault();

    if (url && url.trim() !== "") {
      window.open(url, "_blank");
      handleClose(e);
      return;
    }

    if (appName === "middlebyuniversity") {
      // Close the menu immediately and show loading snackbar
      handleClose(e);
      setSnackbar({
        open: true,
        message: "Please wait while we check your account...",
      });

      try {
        const result = await getAbsorbLmsLogin();

        if (result.data?.url) {
          // Hide loading snackbar
          handleSnackbarClose();
          // Check if user exists in Absorb LMS
          if (result.data.isUserExistsInAbsorb) {
            // User exists, proceed with direct navigation
            window.open(result.data.url, "_blank");
          } else {
            // User doesn't exist, show confirmation dialog
            setRedirectDialog({
              open: true,
              url: result.data?.url || "",
            });
          }
        }
      } catch (error) {
        console.error("Failed to get absorb login URL:", error);
        // Hide loading snackbar on error
        handleSnackbarClose();
      }
    }
  };
  return (
    <>
      <IconButton
        onClick={handleToggle}
        ref={anchorRef}
        size="small"
        sx={{
          padding: 1,
          color: "rgba(0,0,0,0.6)",
        }}
      >
        <MoreMenuOutlined />
      </IconButton>
      <HeaderPopup anchorEl={anchorRef.current} open={open} handleClose={handleClose}>
        <Box sx={{ flex: 1, p: 2 }}>
          <Grid container direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <Grid>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  ml: 1,
                  fontSize: "18px",
                }}
              >
                Apps
              </Typography>
            </Grid>
            <Grid container>
              {coreApps
                .filter((app) => app.name?.toLowerCase() === "masterdata")
                .map(({ id, applicationURL, displayName }) => (
                  <Grid key={id}>
                    <Tooltip title={displayName} placement="top" arrow>
                      <IconButton size="small" sx={{ padding: 1 }} onClick={handleAppClick(applicationURL)}>
                        <MasterDataOutlined color="primary" />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                ))}
              {(role === "Admin" || user?.role?.name === "Admin") && (
                <Grid>
                  <Tooltip title="Admin Center" placement="top" arrow>
                    <IconButton size="small" sx={{ padding: 1 }} onClick={handleAppClick(import.meta.env.VITE_ADMIN_URL)}>
                      <SettingOutlined color="primary" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}

              <Grid>
                <Tooltip title="Home" placement="top" arrow>
                  <IconButton
                    size="small"
                    sx={{ padding: 1 }}
                    onClick={handleAppClick(import.meta.env.VITE_LANDING_PAGE_URL)}
                  >
                    <HomeOutlined color="primary" />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>

          <CircularLoading isLoading={isFetching}>
            <Grid container direction="row">
              <Stack sx={{ pt: 2, flex: 1 }}>
                {normalApps?.map(({ id, applicationURL, displayName, logoURL, name }) => (
                  <ListItemButton
                    sx={(theme) => ({
                      color: theme.palette.grey[800],
                    })}
                    key={id}
                    onClick={handleAppClick(applicationURL, name)}
                  >
                    <ApplicationAvatar logoLink={logoURL} onlyAvatar />
                    <ListItemText
                      primary={displayName}
                      color="text.primary"
                      sx={{
                        marginLeft: 1,
                      }}
                    />
                  </ListItemButton>
                ))}
              </Stack>
            </Grid>
          </CircularLoading>
        </Box>
      </HeaderPopup>

      <AbsorbLmsRedirectDialog
        open={redirectDialog.open}
        userEmail={String(loggedUserEmail) || "your email address"}
        onConfirm={handleRedirectConfirm}
        onCancel={handleRedirectDialogClose}
      />

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={SNACKBAR_SEVERITY.INFO}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};
